import { InfoIcon } from '@phosphor-icons/react';
import { Badge, Card, Tooltip } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import type { ReactNode } from 'react';
import { tooltipTexts } from '@utility/constants/common';
import type { StorageProviderDetailType } from 'types/storage-provider/detail';
import {
  getProviderIntegrationName,
  IntegrationType,
} from '../../lib/integration';
import { ProviderMark } from '../ProviderMark';

/* The list leaves these unlabelled too: the name or the logo already says it. */
const UNLABELLED_INTEGRATIONS: string[] = [
  IntegrationType.manual,
  IntegrationType.spapi,
  IntegrationType.tactical3pl,
];

export type CartonVolume = { total_CBM?: number; total_CBF?: number };

const volume = (value?: number) =>
  CommonUtility.decimalWithCommas(CommonUtility.roundNumber(value));

/** Total carton volume in the provider's units; the other unit on hover. */
function VolumeBadge({
  cbm,
  imperial,
}: {
  cbm: CartonVolume;
  imperial: boolean;
}) {
  const [unit, value, otherUnit, otherValue] = imperial
    ? ['CBF', cbm.total_CBF, 'CBM', cbm.total_CBM]
    : ['CBM', cbm.total_CBM, 'CBF', cbm.total_CBF];

  return (
    <Tooltip
      content={
        <span className="sk:flex sk:flex-col sk:gap-1">
          <span>{imperial ? tooltipTexts.cbf : tooltipTexts.cbm}</span>
          <span>
            {otherUnit} = {volume(otherValue)}
          </span>
        </span>
      }
    >
      <span className="sk:inline-flex">
        <Badge>
          {unit} {volume(value)}
          <InfoIcon size={12} aria-hidden />
        </Badge>
      </span>
    </Tooltip>
  );
}

type ProviderSummaryCardProps = {
  name: string;
  logoUrl?: string;
  countryCode?: string;
  /** The badges after the name: kind, place, volume or units. */
  children?: ReactNode;
};

/** The provider's mark and name over its details, its badges beside them; legacy and V2 pages alike. */
export function ProviderSummaryCard({
  name,
  logoUrl,
  countryCode,
  children,
}: ProviderSummaryCardProps) {
  return (
    <Card bodyClassName="sk:flex sk:items-center sk:gap-4">
      <ProviderMark size="lg" logoUrl={logoUrl} countryCode={countryCode} />
      <h2 className="sk:m-0 sk:min-w-0 sk:truncate sk:text-sm sk:font-semibold">
        {name}
      </h2>
      <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-2">
        {children}
      </div>
    </Card>
  );
}

type ProviderSummaryProps = {
  data: StorageProviderDetailType;
  isFBA: boolean;
  /** Absent for a provider that holds no cartons. */
  cbm?: CartonVolume | null;
  imperial: boolean;
};

/** The provider's name, kind, place and carton volume over its details. */
export function ProviderSummary({
  data,
  isFBA,
  cbm,
  imperial,
}: ProviderSummaryProps) {
  const integration = UNLABELLED_INTEGRATIONS.includes(data.integration_type)
    ? ''
    : getProviderIntegrationName(data.integration_type);

  return (
    <ProviderSummaryCard
      name={data.name}
      logoUrl={data.wmsOrganizationLogo?.url}
      countryCode={data.address?.country}
    >
      {integration ? <Badge>{integration}</Badge> : null}
      {!isFBA && data.address ? (
        <Badge>{CommonUtility.addressShortText(data.address)}</Badge>
      ) : null}
      {cbm ? <VolumeBadge cbm={cbm} imperial={imperial} /> : null}
    </ProviderSummaryCard>
  );
}
